/**
 * A small IndexedDB cache for check reports, keyed by the content-addressed
 * level key. Revisiting a level whose dependencies have not changed costs one
 * lookup instead of a full re-check.
 */

import type { CheckReport } from '../engine/protocol';

const DATABASE = 'lambda-game';
const STORE = 'reports';

/**
 * The schema version, which changes whenever the shape of a stored report does.
 *
 * Bumping it makes the browser open a new database and leave the old one alone,
 * so a report written by an earlier build is never read back into a build that
 * expects something else from it. The shape is checked on the way out as well,
 * because a tab running the older build can still be writing to the same store.
 */
const VERSION = 2;

/**
 * How many reports to keep before starting over.
 *
 * Keys change with every edit, so without a limit the store grows for as long
 * as the player keeps playing. One entry costs a re-check the next time it is
 * wanted, which is milliseconds, so clearing is much cheaper than the bookkeeping
 * an eviction order would need.
 */
const MAX_REPORTS = 400;

let database: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  database ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE, VERSION);
    request.addEventListener('upgradeneeded', () => {
      const db = request.result;
      if (db.objectStoreNames.contains(STORE)) {
        // A report from an older schema is not what this build reads, and the
        // one it would hand back is checked and rejected anyway; starting the
        // store empty says so once rather than on every read.
        request.transaction?.objectStore(STORE).clear();
      } else {
        db.createObjectStore(STORE);
      }
    });
    request.addEventListener('success', () => resolve(request.result));
    request.addEventListener('error', () => {
      // A failed open is not remembered: a browser in a passing mood (private
      // mode, a transient quota) deserves another chance rather than a cache
      // that stays broken for the whole session.
      database = null;
      reject(request.error ?? new Error('could not open the cache'));
    });
  });
  return database;
}

function transaction<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = run(tx.objectStore(STORE));
        request.addEventListener('success', () => resolve(request.result));
        request.addEventListener('error', () =>
          reject(request.error ?? new Error('cache request failed')),
        );
        // A transaction can also abort without its request reporting an error,
        // which would otherwise leave the caller waiting forever.
        tx.addEventListener('abort', () => reject(tx.error ?? new Error('cache request aborted')));
      }),
  );
}

/** The outcome statuses this build can name, and the switches that read them. */
const STATUSES: readonly string[] = ['pass', 'fail', 'limit_exceeded', 'error'];

/**
 * Whether a stored report is one this build can show.
 *
 * The check is on the fields the report is read for, not on every field it has:
 * a report from before the steps were counted has cases without them, and a
 * case without a cost would be shown as `NaN` rather than as a missing number.
 * A status this build does not know is the same kind of question: the outcome
 * switches are exhaustive over the four it does know, so an older name would
 * leave a case with no verdict.
 */
export function isCurrentReport(stored: unknown): stored is CheckReport {
  if (typeof stored !== 'object' || stored === null) {
    return false;
  }
  const cases = (stored as { cases?: unknown }).cases;
  return (
    Array.isArray(cases) &&
    cases.every((entry) => {
      if (typeof entry !== 'object' || entry === null) {
        return false;
      }
      const case_ = entry as { id?: unknown; steps?: unknown; outcome?: unknown };
      if (typeof case_.id !== 'string' || typeof case_.steps !== 'number') {
        return false;
      }
      const outcome = case_.outcome;
      return (
        typeof outcome === 'object' &&
        outcome !== null &&
        STATUSES.includes((outcome as { status?: string }).status ?? '')
      );
    })
  );
}

/** Reads a cached report, or `undefined` when it is not there or is stale. */
export async function readReport(key: string): Promise<CheckReport | undefined> {
  try {
    const stored = await transaction<unknown>('readonly', (store) => store.get(key));
    return isCurrentReport(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

/** Stores a report, starting the cache over when it has grown large. */
export async function writeReport(key: string, report: CheckReport): Promise<void> {
  try {
    // One transaction, so a concurrent tab cannot slip a fresh report in
    // between the count and the clearing of the store.
    await transaction('readwrite', (store) => {
      const counted = store.count();
      counted.addEventListener('success', () => {
        if (counted.result >= MAX_REPORTS) {
          store.clear();
        }
      });
      return store.put(report, key);
    });
  } catch {
    // A full or unavailable cache only costs time, never correctness.
  }
}

/** Empties the cache. */
export async function clearReports(): Promise<void> {
  try {
    await transaction('readwrite', (store) => store.clear());
  } catch {
    // Ignored for the same reason as above.
  }
}
